import React, { useRef, useState } from 'react';
import { generateTimeSlots, getTimeFromPosition } from '../../utils/timeUtils';
import { CALENDAR_SETTINGS } from '../../utils/constants';
import { getRoomBackgroundColor } from '../../utils/colors';
import ReservationBlock from './ReservationBlock';

// ヘッダーの高さ（週表示モード）
const HEADER_HEIGHT = 80;

/**
 * 会議室列コンポーネント（カレンダーの各列）
 */
const RoomColumn = ({
  roomType,
  roomName,
  date,
  dateLabel,
  reservations,
  gbReservations = [], // G+B予約（G列のみで使用）
  onReservationClick,
  onEmptySlotClick,
  onReservationDrop,
  isFirstColumn = false, // G列かどうか
  newlyCreatedIds = [],
}) => {
  const columnRef = useRef(null);
  const timeSlots = generateTimeSlots();
  const { GRID_ROW_HEIGHT } = CALENDAR_SETTINGS;
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragPreviewPosition, setDragPreviewPosition] = useState(null);

  /**
   * 空きスロットクリック時の処理
   */
  const handleSlotClick = (e) => {
    if (e.target.classList.contains('time-grid-cell')) {
      const rect = columnRef.current.getBoundingClientRect();
      // ヘッダーの高さを引いて、グリッド内の実際の位置を取得
      const offsetY = e.clientY - rect.top - HEADER_HEIGHT;
      const time = getTimeFromPosition(offsetY, date);

      if (onEmptySlotClick) {
        onEmptySlotClick({
          roomType,
          date,
          time,
        });
      }
    }
  };

  /**
   * ドラッグオーバー時の処理
   */
  const handleDragOver = (e) => {
    e.preventDefault();

    // ドラッグプレビューの位置を計算
    if (columnRef.current) {
      const rect = columnRef.current.getBoundingClientRect();
      // ヘッダーの高さを引いて、グリッド内の実際の位置を取得
      const offsetY = e.clientY - rect.top - HEADER_HEIGHT;
      const dropTime = getTimeFromPosition(offsetY, date);

      // ドラッグ中の予約データを取得
      try {
        const dragDataStr = e.dataTransfer.getData('reservation');
        if (dragDataStr) {
          const dragData = JSON.parse(dragDataStr);
          const startTime = new Date(dragData.start);
          const endTime = new Date(dragData.end);
          const duration = endTime - startTime;
          const newEndTime = new Date(dropTime.getTime() + duration);

          // 営業時間チェック (7:00-22:00)
          const { START_HOUR, END_HOUR } = CALENDAR_SETTINGS;
          const dropHour = dropTime.getHours();
          const endHour = newEndTime.getHours();
          const endMinute = newEndTime.getMinutes();

          // 営業時間外の場合はドロップを許可しない
          if (
            dropHour < START_HOUR ||
            dropHour >= END_HOUR ||
            endHour > END_HOUR ||
            (endHour === END_HOUR && endMinute > 0)
          ) {
            e.dataTransfer.dropEffect = 'none';
            setDragPreviewPosition(null);
            setIsDragOver(false);
            return;
          }

          e.dataTransfer.dropEffect = 'move';

          // プレビュー位置を計算
          const calendarStart = new Date(date);
          calendarStart.setHours(START_HOUR, 0, 0, 0);
          const minutesFromStart = (dropTime - calendarStart) / (1000 * 60);
          const top = (minutesFromStart / 60) * GRID_ROW_HEIGHT;
          const height = (duration / (1000 * 60 * 60)) * GRID_ROW_HEIGHT;

          setDragPreviewPosition({ top, height });
        }
      } catch (err) {
        // データがまだ利用できない場合は無視
      }
    }

    setIsDragOver(true);
  };

  /**
   * ドラッグリーブ時の処理
   */
  const handleDragLeave = (e) => {
    // 子要素へのドラッグリーブは無視
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setIsDragOver(false);
    setDragPreviewPosition(null);
  };

  /**
   * ドロップ時の処理
   */
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    setDragPreviewPosition(null);

    if (onReservationDrop) {
      const rect = columnRef.current.getBoundingClientRect();
      // ヘッダーの高さを引いて、グリッド内の実際の位置を取得
      const offsetY = e.clientY - rect.top - HEADER_HEIGHT;
      const dropTime = getTimeFromPosition(offsetY, date);

      const dragData = JSON.parse(e.dataTransfer.getData('reservation'));

      onReservationDrop({
        reservation: dragData,
        newRoomType: roomType,
        newTime: dropTime,
      });
    }
  };

  return (
    <div
      ref={columnRef}
      className={`room-column ${isFirstColumn ? 'first-column' : 'second-column'} ${isDragOver ? 'drag-over' : ''}`}
      style={{
        backgroundColor: getRoomBackgroundColor(roomType),
        position: 'relative',
      }}
      onClick={handleSlotClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      aria-label={`${roomName} ${dateLabel || ''} - クリックして予約を作成`}
    >
      <div className="room-header weekly">
        <div className="room-name">{roomName}</div>
      </div>

      <div className="time-grid">
        {timeSlots.map((time) => (
          <div
            key={time}
            className="time-grid-cell"
            style={{
              height: `${GRID_ROW_HEIGHT / 4}px`,
              borderTop: time.endsWith(':00') ? '1px solid #ccc' : '1px solid #eee',
            }}
          />
        ))}
      </div>

      {/* 予約ブロックを配置 */}
      <div className="reservations-layer">
        {/* 通常予約（GまたはB） */}
        {reservations.map((reservation) => (
          <ReservationBlock
            key={reservation.reservation_id}
            reservation={reservation}
            date={date}
            onClick={() => onReservationClick && onReservationClick(reservation)}
            isSpanning={false}
            isNewlyCreated={newlyCreatedIds.includes(reservation.id)}
          />
        ))}

        {/* G+B予約（G列でのみ表示、2列跨ぎ） */}
        {isFirstColumn && gbReservations.map((reservation) => (
          <ReservationBlock
            key={reservation.reservation_id}
            reservation={reservation}
            date={date}
            onClick={() => onReservationClick && onReservationClick(reservation)}
            isSpanning={true}
            isNewlyCreated={newlyCreatedIds.includes(reservation.id)}
          />
        ))}

        {/* ドラッグプレビュー */}
        {dragPreviewPosition && (
          <div
            className="drag-preview"
            style={{
              position: 'absolute',
              top: `${dragPreviewPosition.top}px`,
              left: '4px',
              right: '4px',
              height: `${dragPreviewPosition.height}px`,
              backgroundColor: 'rgba(59, 130, 246, 0.3)',
              border: '2px dashed #3b82f6',
              borderRadius: '4px',
              pointerEvents: 'none',
              zIndex: 999,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default RoomColumn;
